import React from 'react';
import { Table } from 'react-bootstrap';
import { ThunderbirdOrdersData } from '../../types/IThunderbirdOrderResponse';
import formatMoney from '../../utils/formatMoney';
import TablePagination, {
  PageSizeChoice,
  tableMinHeight,
} from '../TablePagination';
import TableStateRow from '../TableStateRow';
import DateTimeText from '../DateTimeText';
import useUrlFilters from '../../hooks/useUrlFilters';

interface ThunderbirdOrdersTableProps {
  data: ThunderbirdOrdersData | undefined;
  handleModalOpen: (data: any) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  isFetching: boolean;
  rowDetails: any;
  setRowDetails: any;
  pageSize?: number;
  onPageSizeChange?: (size: PageSizeChoice) => void;
  isAutoPageSize?: boolean;
  fitPageSize?: number;
}

const COLUMNS = 7;

const ThunderbirdOrdersTable: React.FC<ThunderbirdOrdersTableProps> = ({
  data,
  handleModalOpen,
  currentPage,
  setCurrentPage,
  isFetching,
  rowDetails,
  setRowDetails,
  pageSize,
  onPageSizeChange,
  isAutoPageSize,
  fitPageSize,
}) => {
  useUrlFilters({
    page: { kind: 'page', value: currentPage, set: setCurrentPage },
  });

  const hasRows = !!data?.items?.length;

  return (
    <div className="data-card">
      <div
        className="table-scroll"
        style={{ minHeight: tableMinHeight(pageSize || 0, false) }}
      >
        <Table bordered={false} className="data-table">
          <thead>
            <tr>
              <th>PID</th>
              <th>Order Date</th>
              <th className="cell-num">Kits</th>
              <th className="cell-num">Kit Price</th>
              <th className="cell-num">Boxes</th>
              <th className="cell-num">Box Price</th>
              <th className="cell-num">Total</th>
            </tr>
          </thead>
          <tbody
            className={isFetching && hasRows ? 'is-refreshing' : undefined}
          >
            {!hasRows ? (
              <TableStateRow
                colSpan={COLUMNS}
                loading={isFetching}
                message="No Thunderbird orders yet."
              />
            ) : (
              data!.items.map((order) => (
                <tr
                  role="button"
                  className={
                    rowDetails?.isActive && rowDetails.orderId === order.id
                      ? 'is-selected'
                      : ''
                  }
                  key={order.id}
                  data-track="order_row"
                  data-track-id={order.pid}
                  onClick={() => {
                    setRowDetails({
                      class: 'is-selected',
                      orderId: order.id,
                      isActive: true,
                    });
                    handleModalOpen({
                      modal: 'thunderbird-order',
                      data: { ...order },
                    });
                  }}
                >
                  <td className="cell-pid">{order.pid}</td>
                  <td className="cell-mono">
                    <DateTimeText value={order.orderDate} />
                  </td>
                  <td className="cell-num">{order.numberOfKits}</td>
                  <td className="cell-num muted">
                    {formatMoney(order.kitPrice)}
                  </td>
                  <td className="cell-num">{order.numberOfBoxes}</td>
                  <td className="cell-num muted">
                    {formatMoney(order.boxPrice)}
                  </td>
                  <td className="cell-num cell-strong">
                    {formatMoney(order.total)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalPages={data?.maxPage}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        isAutoPageSize={isAutoPageSize}
        fitPageSize={fitPageSize}
        total={data?.total}
        itemCount={data?.items?.length ?? 0}
        noun="orders"
      />
    </div>
  );
};

export default ThunderbirdOrdersTable;
